import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, RefreshCw, AlertTriangle,  History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReportTypeSelector from './components/ReportTypeSelector';
import ReportFilters from './components/ReportFilters';
import RecentJobsTabs from './components/RecentJobsTabs';
import api from '../../../../api/client';
import useJobs from './components/useJobs';
import ActiveJobCard from './components/ActiveJobCards';
import CustomToastContainer from '../../../CustomToastContainer';
import { toast } from 'react-toastify';
import { theme } from '../../../../theme';

const getDefaultDates = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const format = (d) => d.toISOString().split('T')[0];
  return { start: format(oneMonthAgo), end: format(today) };
};

const ReportGeneration = () => {
  const { t } = useTranslation();
  const [selectedReportType, setSelectedReportType] = useState('');
  const defaultDates = getDefaultDates();
  const [filters, setFilters] = useState({
    format: 'pdf',
    template: 'basic',
    start_date: defaultDates.start,
    end_date: defaultDates.end
  });

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const userRole = localStorage.getItem('role');

  useEffect(() => {
    if (userRole === 'Super Admin') {
      const fetchCompaniesList = async () => {
        try {
          const res = await api.get('companies/');
          const companyData = res.data.results || res.data;
          setCompanies(Array.isArray(companyData) ? companyData : []);
        } catch (err) {
          console.error('Failed to fetch companies:', err);
        }
      };
      fetchCompaniesList();
    }
  }, [userRole]);

  const {
    candidatesJobs,
    interviewsJobs,
    pendingJobs,
    completedJobs,
    failedJobs,
    currentJobs,
    currentPage,
    totalPages,
    totalCount,
    currentCount,
    pageSize,
    activeTab,
    handlePageChange,
    handlePageSizeChange,
    handleTabChange,
    loading,
    error,
    refreshJobs,
    isPolling,
  } = useJobs(selectedCompanyId);

  const handleGenerateReport = async () => {
    if (!selectedReportType) {
      toast.error(t('report.pleaseSelectReportType'), { style: { backgroundColor: theme.colors.error[500], color: theme.colors.white } });
      return;
    }

    try {
      const requestData = {
        report_type: selectedReportType,
        format: filters.format,
        template: filters.template,
        filters: {
          ...(filters.start_date && { start_date: filters.start_date }),
          ...(filters.end_date && { end_date: filters.end_date }),
          ...(selectedCompanyId && { company_id: selectedCompanyId })
        }
      };
      await api.post('reports/export/', requestData);

      toast.success(t('report.reportGenerationStarted'), { style: { backgroundColor: theme.colors.success[500], color: theme.colors.white } });
      await refreshJobs();

      // Optional: smooth scroll to active jobs
      window.scrollTo({ top: 300, behavior: 'smooth' });

    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(t('report.failedToStartGeneration', { error: error.message }), { style: { backgroundColor: theme.colors.error[500], color: theme.colors.white } });
    }
  };

  const handleDownload = async (jobId) => {
    try {
      const response = await api.get(`reports/download/${jobId}/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;

      const job = [...candidatesJobs, ...interviewsJobs].find(j => j.id === jobId);
      const extension = job?.format === 'pdf' ? 'pdf' : job?.format === 'excel' ? 'xlsx' : 'csv';
      const filename = `${job?.report_type || 'report'}_${jobId}.${extension}`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('report.reportDownloaded', { filename }), { style: { backgroundColor: theme.colors.success[500], color: theme.colors.white } });
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('report.failedToDownload', { error: error.message }), { style: { backgroundColor: theme.colors.error[500], color: theme.colors.white } });
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await api.delete(`reports/jobs/${jobId}/`);
      toast.warn(t('report.reportGenerationCancelled'), { style: { backgroundColor: theme.colors.warning[500], color: theme.colors.white } });
      refreshJobs();
    } catch (error) {
      console.error('Cancel job error:', error);
      toast.error(t('report.failedToCancel', { error: error.message }), { style: { backgroundColor: theme.colors.error[500], color: theme.colors.white } });
    }
  };

  const handleReportTypeChange = (newType) => {
    setSelectedReportType(newType);
    if (newType === 'candidates') {
      handleTabChange('candidates');
    } else if (newType === 'interviews') {
      handleTabChange('interviews');
    }
  };

  const completedCandidatesJobs = candidatesJobs.filter(job => job.status === 'completed');
  const completedInterviewsJobs = interviewsJobs.filter(job => job.status === 'completed');
  const failedCandidatesJobs = candidatesJobs.filter(job => job.status === 'failed');
  const failedInterviewsJobs = interviewsJobs.filter(job => job.status === 'failed');
  const completedAndFailedJobs = [...completedJobs, ...failedJobs];

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50/50 backdrop-blur-sm">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center">
          <RefreshCw className="h-10 w-10 animate-spin text-gray-400 mb-4" />
          <p className="text-gray-500 font-medium tracking-tight text-sm uppercase">{t('report.loadingReports')}</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md p-8 bg-white rounded-3xl shadow-xl">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('report.errorTitle')}</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button onClick={refreshJobs} className="w-full rounded-xl bg-gray-900 text-white px-4 py-3 font-medium hover:bg-black transition-colors">
            {t('report.retry')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-body" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pt-3">
        {/* Header */}
        <div className="mb-4">
          <div
            className="w-full px-4 py-3 rounded-xl text-white"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold mb-0.5 text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('report.pageTitle')}
                </h1>
                <p className="text-xs text-blue-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('report.pageSubtitle')}
                </p>
              </div>

              {userRole === 'Super Admin' && (
                <div className="min-w-[240px]">
                  <select
                    value={selectedCompanyId}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm bg-white text-blue-600 border-0 shadow-sm"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    <option value="">{t('dashboard.allCompanies')}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">

        {/* 2. The Generator (Creation Flow) */}
        <section className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <ReportTypeSelector
              selectedType={selectedReportType}
              onTypeChange={handleReportTypeChange}
            />
          </motion.div>

          <AnimatePresence mode="wait">
            {selectedReportType && (
              <motion.div
                key="filters"
                initial={{ opacity: 0, y: 20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -14, height: 0 }}
                transition={{ duration: 0.32, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div
                  className="
            mt-6 relative rounded-3xl
            border border-gray-200/60
            bg-gradient-to-br from-white/80 to-gray-50/60
            backdrop-blur-md
            shadow-[0_8px_35px_-8px_rgba(0,0,0,0.12)]
            transition-all duration-300
            hover:shadow-[0_12px_45px_-6px_rgba(0,0,0,0.16)]
            px-8 py-10 sm:px-10 sm:py-12
          "
                >
                  {/* Soft ambient highlight */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-10 right-6 w-40 h-40 rounded-full bg-blue-500/10 blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-purple-400/10 blur-2xl" />
                  </div>

                  {/* Header */}
                  <div className="relative z-10 mb-10 flex items-center gap-3">
                    <div className="w-2 h-8 rounded-full bg-gray-900 opacity-80" />
                    <h2 className="text-lg font-semibold text-gray-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {t('report.configureReport')}
                    </h2>
                  </div>

                  {/* Filters */}
                  <div className="relative z-10">
                    <ReportFilters
                      filters={filters}
                      onFiltersChange={setFilters}
                      onGenerateReport={handleGenerateReport}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>



        {/* 3. Active Jobs (Current Tasks) */}
        <AnimatePresence>
          {pendingJobs.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="mb-8"
            >
              <div className="flex items-center justify-between mb-6 px-2">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <RefreshCw size={14} className="animate-spin" />
                  {t('report.processingNow')}
                </h3>
              </div>
              <div className="space-y-4">
                {pendingJobs.map(job => (
                  <ActiveJobCard
                    key={job.id}
                    job={job}
                    onDownload={handleDownload}
                    onCancel={handleCancelJob}
                  />
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* 4. The Archive (History) */}
        <section className="relative">
          <div className="flex items-center justify-between mb-1 px-2 border-b border-gray-100 pb-2">
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <History size={14} />
              {t('report.reportHistory')}
            </h3>
            <button
              onClick={refreshJobs}
              className="p-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
            >
              <RefreshCw size={16} className={isPolling ? "animate-spin" : ""} />
            </button>
          </div>

          {(completedAndFailedJobs.length > 0) ? (
            <div>
              <RecentJobsTabs
                jobs={currentJobs}
                onDownload={handleDownload}
                onCancel={handleCancelJob}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                currentCount={currentCount}
                pageSize={pageSize}
                activeTab={activeTab}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onTabChange={handleTabChange}
                candidatesCount={completedCandidatesJobs.length + failedCandidatesJobs.length}
                interviewsCount={completedInterviewsJobs.length + failedInterviewsJobs.length}
              />
            </div>
          ) : (
            pendingJobs.length === 0 && (
              <div className="text-center py-24 opacity-40">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-widest">{t('report.noHistoryYet')}</p>
              </div>
            )
          )}
        </section>

      </main>
      <CustomToastContainer />
    </div>
  );
};

export default ReportGeneration;
